import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

export default function LoginPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data);
      if (data.role === "admin" && !data.business_id) navigate("/admin");
      else navigate(data.business_id ? "/dashboard" : "/business-profile");
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="login-page">
      <SEO title="Log In to A8A" description="Access your A8A dashboard to view your business diagnostic findings and recommended next steps." noindex />
      <form onSubmit={handleSubmit} className="w-full max-w-md space-y-5 border border-border p-8 bg-card">
        <h1 className="font-heading text-2xl font-medium text-foreground">Log in to A8A</h1>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="login-email-input" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="login-password-input" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="login-error-message">{error}</p>}
        <Button type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit-button">
          {loading ? "Logging in…" : "Log In"}
        </Button>
        <p className="text-sm text-center">
          <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground underline" data-testid="forgot-password-link">
            Forgot your password?
          </Link>
        </p>
        <p className="text-sm text-muted-foreground text-center">
          New to A8A? <Link to="/scan" className="text-accent underline">Start the scan</Link>
        </p>
      </form>
    </div>
  );
}
